// Group colours are user data (hex strings such as "#3B82F6", "3b82f6" or "#38f").
// groupPillVars returns the CSS custom properties a tinted group pill needs:
//   --group-color: the colour itself (dot, tint and hairline)
//   --group-ink:   a darker shade of the same hue, readable on the 13% tint
// It returns null for a missing or unparseable colour, so callers fall back to
// the violet tag style.

const parseHex = (value) => {
  if (typeof value !== 'string') return null;
  let hex = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((ch) => ch + ch).join('');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
};

const toHex = (rgb) => `#${rgb.map((n) => Math.round(n).toString(16).padStart(2, '0')).join('')}`;

const rgbToHsl = ([r, g, b]) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h / 6, s, l];
};

const hslToRgb = ([h, s, l]) => {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const hue = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)].map((v) => v * 255);
};

// Darken to at most 30% lightness (keeps the hue; caps saturation so very
// saturated colours do not look neon as text). Contrast on the light tint is
// above 4.5:1 for all hues at this lightness.
export const groupInk = (rgb) => {
  const [h, s, l] = rgbToHsl(rgb);
  return toHex(hslToRgb([h, Math.min(s, 0.8), Math.min(l, 0.3)]));
};

export const groupPillVars = (color) => {
  const rgb = parseHex(color);
  if (!rgb) return null;
  return { '--group-color': toHex(rgb), '--group-ink': groupInk(rgb) };
};
